import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import {
    PayinPaymentSwitchingResponse,
    PayinPaymentSwitchingResponseCodes,
    PayinPayoutSwitchingDataService,
    PaymentSwitchingData,
    PayinPaymentSwitchingRequest,
    ScoringSyncFacade
} from '@p2p/payment/domain';
import { catchError, EMPTY, Observable, skipWhile, switchMap, take, tap } from 'rxjs';

export interface PayinPayoutSwitchingStoreState {
    switchingData: PaymentSwitchingData | null;
    switchingDataLoading: boolean;
    switchingDataError: HttpErrorResponse | null;
}

const initialState: PayinPayoutSwitchingStoreState = {
    switchingData: null,
    switchingDataLoading: true,
    switchingDataError: null
};

@Injectable()
export class PayinPayoutSwitchingStore extends ComponentStore<PayinPayoutSwitchingStoreState> {
    private readonly payinPayoutSwitchingDataService = inject(PayinPayoutSwitchingDataService);
    private readonly payinScoringSyncFacade = inject(ScoringSyncFacade);

    readonly switchingData$ = this.select((state) => state.switchingData);
    readonly switchingDataLoading$ = this.select((state) => state.switchingDataLoading);
    readonly switchingDataError$ = this.select((state) => state.switchingDataError);

    get responseEndEvent$(): Observable<HttpErrorResponse | null> {
        return this.switchingDataLoading$.pipe(
            skipWhile((loading) => loading),
            switchMap(() => this.switchingDataError$),
            take(1)
        );
    }

    readonly loadPaymentSwitchingData = this.effect((params$: Observable<void>) =>
        params$.pipe(
            tap(() => this.patchState({ switchingDataLoading: true })),
            switchMap(() =>
                this.payinPayoutSwitchingDataService.getPaymentDirectionActivitySettings().pipe(
                    tap({
                        next: (response) => {
                            const switchingData = this.transformSwitchingData(response);

                            this.payinScoringSyncFacade.setData(switchingData);
                            this.patchState({ switchingData, switchingDataLoading: false });
                        },
                        error: () => this.patchState({ switchingDataLoading: false })
                    }),
                    catchError(() => EMPTY)
                )
            )
        )
    );

    readonly changeSwitchingData = this.effect((data$: Observable<PaymentSwitchingData>) =>
        data$.pipe(
            tap(() => this.patchState({ switchingDataLoading: true })),
            switchMap((data) => {
                const request: PayinPaymentSwitchingRequest = {
                    payinActive: data.payinActive ? 'true' : 'false',
                    payoutActive: data.payoutActive ? 'true' : 'false'
                };

                return this.payinPayoutSwitchingDataService.changePaymentDirectionActivitySettings(request).pipe(
                    tap({
                        next: () => {
                            this.payinScoringSyncFacade.setData(data);
                            this.patchState({ switchingData: data, switchingDataLoading: false });
                        },
                        error: () => this.patchState({ switchingDataLoading: false })
                    }),
                    catchError(() => EMPTY)
                );
            })
        )
    );

    private transformSwitchingData(response: PayinPaymentSwitchingResponse): PaymentSwitchingData {
        const result: PaymentSwitchingData = {
            payinActive: false,
            payoutActive: false
        };

        response.paymentDirectionActivitySettings.forEach((setting) => {
            if (setting.code === PayinPaymentSwitchingResponseCodes.PayinActive) {
                result.payinActive = setting.value == 'true';
            } else if (setting.code === PayinPaymentSwitchingResponseCodes.PayoutActive) {
                result.payoutActive = setting.value == 'true';
            }
        });

        return result;
    }

    constructor() {
        super(initialState);
    }
}
